import { useState } from 'react'
import type { AuthoringCatalog } from '../../lib/api'
import { type DetectionRuleDraft, parseTerms } from './policyDocument'

interface Props {
  rules: DetectionRuleDraft[]
  redactMatchedText: boolean
  catalog: AuthoringCatalog
  onChange: (rules: DetectionRuleDraft[], redactMatchedText: boolean) => void
}

const SEVERITY_RANK: Record<string, number> = { low: 2, medium: 3, high: 4, critical: 5 }

/**
 * Rules written into the policy document itself, rather than merged from a dictionary.
 *
 * This is the second-choice path and is folded away for that reason: a rule written here belongs to
 * one policy, nobody else can correct its terms, and correcting them reaches no other policy that
 * needs the same fix. It exists because a document may arrive carrying rules — hand-written, or from
 * an older revision — and something an operator cannot see is something they cannot remove.
 *
 * Content and signal rules share one list because the agent's uniqueness check spans both. Two lists
 * side by side would let someone add a signal rule named "credentials" and only find out when every
 * device in the fleet rejected the policy.
 */
export function DetectionRuleEditor({ rules, redactMatchedText, catalog, onChange }: Props) {
  const [selected, setSelected] = useState(0)

  const rule = rules[selected] as DetectionRuleDraft | undefined
  const contentCount = rules.filter((entry) => entry.kind === 'content').length
  const signalCount = rules.length - contentCount
  const atContentLimit = contentCount >= catalog.limits.maximumContentRules
  const atSignalLimit = signalCount >= catalog.limits.maximumSignalRules

  const replace = (index: number, next: DetectionRuleDraft) =>
    onChange(rules.map((entry, position) => (position === index ? next : entry)), redactMatchedText)

  const duplicateName = (name: string, index: number) =>
    rules.some((entry, position) => position !== index && entry.name.toLowerCase() === name.trim().toLowerCase())

  const addBlank = () => {
    onChange(
      [...rules, { kind: 'content', name: '', matchAny: [], severity: 'high', caseSensitive: false, wholeWord: false }],
      redactMatchedText
    )
    setSelected(rules.length)
  }

  const remove = (index: number) => {
    onChange(rules.filter((_, position) => position !== index), redactMatchedText)
    setSelected(Math.max(0, index - 1))
  }

  return (
    <div className="rule-editor" data-testid="detection-rules">
      <div className="rule-list">
        <ul>
          {rules.map((entry, index) => (
            <li key={`${entry.name}-${index}`}>
              <button
                type="button"
                className={index === selected ? 'rule-row selected' : 'rule-row'}
                onClick={() => setSelected(index)}
                data-testid={`detection-rule-${index}`}
              >
                <span className="rule-row-name">{entry.name || 'Unnamed rule'}</span>
                <span className="hint">
                  {entry.kind === 'signal' ? 'Signal' : entry.severity} · {entry.matchAny.length} term
                  {entry.matchAny.length === 1 ? '' : 's'}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {rules.length === 0 && (
          <p className="hint" data-testid="no-detection-rules">
            None, which is the usual answer. Bind a dictionary above instead — a rule written here
            belongs to this policy alone and nobody else can correct its terms.
          </p>
        )}

        <div className="toolbar">
          <button type="button" onClick={addBlank} disabled={atContentLimit} data-testid="add-blank-rule">
            New rule
          </button>
        </div>

        <label className="inline-check">
          <input
            type="checkbox"
            checked={redactMatchedText}
            onChange={(event) => onChange(rules, event.target.checked)}
            data-testid="redact-matched-text"
          />
          <span>Redact the matched words before storing them</span>
        </label>

        <p className="hint">
          {contentCount} of {catalog.limits.maximumContentRules} content rules, {signalCount} of{' '}
          {catalog.limits.maximumSignalRules} signal rules.
        </p>
        {(atContentLimit || atSignalLimit) && (
          <p className="hint warn">The agent rejects a policy that goes past these, so adding another is refused.</p>
        )}
      </div>

      {rule && (
        <div className="rule-form">
          <div className="field">
            <label htmlFor="rule-kind">Kind</label>
            <select
              id="rule-kind"
              value={rule.kind}
              onChange={(event) =>
                replace(
                  selected,
                  event.target.value === 'signal'
                    ? { kind: 'signal', name: rule.name, matchAny: rule.matchAny, caseSensitive: rule.caseSensitive }
                    : {
                        kind: 'content',
                        name: rule.name,
                        matchAny: rule.matchAny,
                        severity: 'high',
                        caseSensitive: rule.caseSensitive,
                        wholeWord: false
                      }
                )
              }
              data-testid="rule-kind"
            >
              <option value="content">Content rule — flag what was typed</option>
              <option value="signal">Signal rule — note the account, plan, or workspace</option>
            </select>
            <div className="hint">
              {rule.kind === 'content'
                ? 'A match raises a detection and can be acted on.'
                : 'A signal is recorded alongside the observation. It never blocks anything.'}
            </div>
          </div>

          <div className="field">
            <label htmlFor="rule-name">Name</label>
            <input
              id="rule-name"
              value={rule.name}
              maxLength={catalog.limits.maximumRuleNameLength}
              onChange={(event) => replace(selected, { ...rule, name: event.target.value })}
              placeholder="wire transfer instructions"
              data-testid="rule-name"
            />
            {!rule.name.trim() && (
              <div className="hint warn" data-testid="unnamed-rule">
                Every rule needs a name. The agent refuses the whole policy without one.
              </div>
            )}
            {duplicateName(rule.name, selected) && (
              <div className="hint warn" data-testid="duplicate-rule-name">
                Another rule already has this name. The agent rejects the whole policy for a duplicate,
                across content and signal rules together.
              </div>
            )}
          </div>

          <div className="field">
            <label htmlFor="rule-terms">Terms</label>
            <textarea
              id="rule-terms"
              className="terms"
              value={rule.matchAny.join('\n')}
              onChange={(event) => replace(selected, { ...rule, matchAny: parseTerms(event.target.value) })}
              placeholder={'routing number\nbeneficiary account\nswift code'}
              data-testid="rule-terms"
            />
            <div className="hint">
              One per line, or separated by commas. Matching is a plain case-insensitive substring test,
              so write phrases: a lone &ldquo;account&rdquo; fires on ordinary conversation. Include the
              abbreviations and the misspellings people actually type — a rule that only matches the
              correct spelling is beaten by a typo.
            </div>
            <div className={rule.matchAny.length === 0 ? 'hint warn' : 'hint'} data-testid="rule-term-count">
              {rule.matchAny.length === 0
                ? 'A rule with no terms is accepted by the device and then fails every time it runs, so this is refused before it can be published.'
                : `${rule.matchAny.length} of ${catalog.limits.maximumTermsPerRule} terms.`}
            </div>
          </div>

          {rule.kind === 'content' && (
            <div className="field">
              <label htmlFor="rule-severity">Severity</label>
              <select
                id="rule-severity"
                value={rule.severity}
                onChange={(event) => replace(selected, { ...rule, severity: event.target.value })}
                data-testid="rule-severity"
              >
                {catalog.severities.map((severity) => (
                  <option key={severity} value={severity}>
                    {severity}
                  </option>
                ))}
              </select>
              <div className="hint">
                <span className={`pill sev-${SEVERITY_RANK[rule.severity] ?? 3}`}>{rule.severity}</span> What is
                chosen under "When a rule matches" happens only at or above the severity it names, so
                this decides whether a match is merely observed or actually stopped.
              </div>
            </div>
          )}

          <label className="inline-check">
            <input
              type="checkbox"
              checked={rule.caseSensitive}
              onChange={(event) => replace(selected, { ...rule, caseSensitive: event.target.checked })}
              data-testid="rule-case-sensitive"
            />
            <span>Case sensitive</span>
          </label>

          {rule.kind === 'content' && (
            <label className="inline-check">
              <input
                type="checkbox"
                checked={rule.wholeWord}
                onChange={(event) => replace(selected, { ...rule, wholeWord: event.target.checked })}
                data-testid="rule-whole-word"
              />
              <span>Whole word only</span>
            </label>
          )}

          <div className="toolbar" style={{ marginTop: '0.85rem' }}>
            <button type="button" onClick={() => remove(selected)} data-testid="remove-rule">
              Remove this rule
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
